import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { UseMutationResult } from '@tanstack/react-query';
import type { ChatbotConversation, ChatbotConversationRenameResponse } from '@/models/chatbot';
import { getHistoryErrorMessageKey } from './historyError';

interface UseRenameConversationParams {
  rename: UseMutationResult<ChatbotConversationRenameResponse, Error, { id: string; title: string }>;
  commitRename: (id: string, title: string) => void;
}

const useRenameConversation = ({ rename, commitRename }: UseRenameConversationParams) => {
  const { t } = useTranslation();

  const [renameTarget, setRenameTarget] = useState<ChatbotConversation | null>(null);
  const [renamedId, setRenamedId] = useState<string | null>(null);
  const [renameError, setRenameError] = useState<string | null>(null);

  const clearRenameError = useCallback(() => {
    setRenameError(null);
  }, []);

  const startRenaming = useCallback(
    (conversation: ChatbotConversation) => {
      clearRenameError();
      setRenameTarget(conversation);
    },
    [clearRenameError],
  );

  const handleRenameSave = useCallback(
    (title: string) => {
      const trimmed = title.trim();

      if (!renameTarget || !trimmed) {
        return;
      }

      const { id } = renameTarget;
      const willAnimate = trimmed !== renameTarget.title.trim();

      clearRenameError();

      rename.mutate(
        { id, title: trimmed },
        {
          onSuccess: () => {
            commitRename(id, trimmed);

            if (willAnimate) {
              setRenamedId(id);
            }

            setRenameTarget(null);
          },
          onError: error => {
            setRenameError(t(getHistoryErrorMessageKey('rename', error)));
          },
        },
      );
    },
    [clearRenameError, commitRename, rename, renameTarget, t],
  );

  const handleRenameAnimationEnd = useCallback((conversationId: string) => {
    setRenamedId(current => (current === conversationId ? null : current));
  }, []);

  const handleRenameCancel = useCallback(() => {
    clearRenameError();
    setRenameTarget(null);
  }, [clearRenameError]);

  return {
    renameTarget,
    renamedId,
    renameError,
    startRenaming,
    handleRenameSave,
    handleRenameCancel,
    handleRenameAnimationEnd,
    clearRenameError,
    isRenamePending: rename.isPending,
  };
};

export default useRenameConversation;
